// Kompletný volebný program Jána Kuráňa pre Ružomberok 2026–2030.
// Verný prepis dodaného dokumentu. Nevymýšľať fakty ani čísla.

export const programMeta = {
  title: "Volebný program Ján Kuráň | Ružomberok 2026",
  description:
    "Kompletný volebný program Jána Kuráňa pre Ružomberok 2026–2030. Práca, bývanie, MHD, verejný priestor, školstvo, sociálne služby, turizmus, klíma, bezpečnosť a fungovanie mesta.",
  lead: "Mesto pre ľudí. Volebný program pre Ružomberok 2026–2030.",
  subLead: "Práca. Bývanie. Dôstojný život. Fungujúce mesto.",
};

export const programIntro = {
  heading: "Ružomberok potrebuje správny smer",
  paragraphs: [
    "Keď som približne pred rokom a pol začal pracovať na tomto programe, nechcel som vytvoriť ďalší zoznam vecí, ktoré sa dajú sľúbiť pred voľbami a o štyri roky na ne zabudnúť. Chcel som najskôr pochopiť, kam sa naše mesto vyvíja a čo bude rozhodovať o tom, ako sa tu bude žiť o desať či dvadsať rokov. Preto som začal dátami.",
    "Ružomberok mal v roku 1995 viac ako 31-tisíc obyvateľov. V roku 2023 ich bolo už len 26 518. Mesto vo svojich vlastných analytických materiáloch upozorňuje na dlhodobý pokles počtu obyvateľov a na nízky počet ľudí v najmladších vekových skupinách.",
    "Aktuálny Index kvality života 2026 zároveň ukazuje mesto s 25 826 obyvateľmi, ktoré skončilo na 36. mieste zo 71 okresných miest Slovenska. Máme silné stránky – prírodu, školy či stále pomerne dobré výsledky pri vývoji populácie mladých ľudí – ale zároveň výrazné rezervy v doprave, dostupnosti lekárov, životnom prostredí aj ďalších oblastiach každodenného života.",
    "Ľudia zostávajú tam, kde sa im dá dobre žiť ako celku. Kde si môžu nájsť bývanie. Kde majú prácu, z ktorej sa dá dôstojne žiť. Kde funguje doprava. Kde sú kvalitné školy a služby. Kde sa mesto stará o verejný priestor. A kde radnica vie, čo chce dosiahnuť a dokáže za tým systematicky ísť.",
  ],
  goals: [
    {
      title: "Hlavný cieľ 1",
      text:
        "Vrátiť Ružomberok späť k hranici 30 000 obyvateľov. Vytvoriť podmienky, aby ľudia nemuseli z mesta odchádzať a aby malo zmysel prísť sem bývať, pracovať, podnikať a založiť si rodinu.",
    },
    {
      title: "Hlavný cieľ 2",
      text:
        "Zmeniť demografický vývoj mesta v prospech mladších ľudí a rodín. Nestačí iba sledovať počet obyvateľov. Rozhodujúce pre budúcnosť mesta je aj to, kto v Ružomberku zostáva, kto odchádza a kto sem prichádza.",
    },
  ],
  note:
    "Ako čítať program: pri opatreniach pôvodný program rozlišuje orientačnú časovú a finančnú náročnosť. Presný rozsah závisí od projektovej prípravy, cien, možností rozpočtu, eurofondov a rozhodnutí mestského zastupiteľstva.",
};

export const chapters = [
  {
    no: "01",
    slug: "praca-a-investicie",
    title: "Práca, investori a silnejšia ekonomika",
    tagline: "Chlieb aj ruže. Práca, z ktorej sa dá dôstojne žiť.",
    intro:
      "Mesto nevytvorí pracovné miesta príkazom. Vie však vytvoriť podmienky, v ktorých sa oplatí investovať, rozširovať, podnikať alebo prinášať pracovné miesta s vyššou pridanou hodnotou.",
    sections: [
      {
        h: "Mestský tím pre investorov",
        items: [
          "Vytvoríme malý profesionálny tím, ktorého jedinou úlohou bude práca s investormi a podnikateľmi.",
          "Každý investor dostane jedného konkrétneho pracovníka mesta, ktorý bude koordinovať komunikáciu s celým úradom.",
          "Zavedieme predkonzultácie investičných zámerov ešte pred formálnym povoľovaním, aby sa problémy riešili na začiatku, nie po mesiacoch.",
          "Budeme systematicky oslovovať firmy, ktoré rozširujú výrobu alebo služby a pre ktoré môže byť Ružomberok vhodnou lokalitou.",
        ],
      },
      {
        h: "Pozemky, haly a brownfieldy",
        items: [
          "Spracujeme databázu voľných alebo rozvojovo využiteľných pozemkov, hál a iných objektov.",
          "Zmapujeme nevyužívané priemyselné areály a budeme rokovať s vlastníkmi o ich novom využití.",
          "Pri perspektívnych objektoch pripravíme dopravné a technické napojenie tak, aby mesto ponúkalo investorovi reálne pripravenú lokalitu.",
          "Budeme aktualizovať územný plán tak, aby vznikali nové plochy pre ľahký priemysel, technológie, logistiku a služby primerané mestu.",
        ],
      },
      {
        h: "Školy, univerzita a firmy",
        items: [
          "Budeme prepájať stredné školy, Katolícku univerzitu a zamestnávateľov pri praxi, stážach a témach záverečných prác.",
          "Podporíme coworking, mentoring, networking a priestory pre začínajúcich podnikateľov a kreatívne profesie.",
        ],
      },
    ],
    benefits: [
      "Viac pracovných príležitostí priamo v Ružomberku.",
      "Silnejšiu konkurenciu medzi zamestnávateľmi a tlak na rast dobre platených pracovných miest.",
      "Väčšiu šancu, že mladí ľudia zostanú alebo sa po škole vrátia.",
      "Radnicu, ktorá investorov aktívne hľadá a sprevádza, nie iba čaká.",
    ],
  },
  {
    no: "02",
    slug: "byvanie",
    title: "Bývanie: 1 000 nových bytov do roku 2036",
    tagline: "Bez dostupného bývania nezastavíme odchod ľudí.",
    intro:
      "Cieľom mesta nebude sľúbiť, že samo postaví tisíc bytov. Cieľom bude vytvoriť podmienky, aby počas desiatich rokov vzniklo približne 1 000 nových bytov kombináciou družstevného, nájomného a súkromného bývania.",
    sections: [
      {
        h: "Družstevné bývanie",
        items: [
          "Pripravíme pilotný projekt družstevného bývania podľa fungujúcich modelov miest, ktoré využívajú mestské pozemky a partnerstvo s bytovým družstvom.",
          "Mesto môže do projektu vstúpiť pozemkom, územnou prípravou alebo infraštruktúrou, nie nevyhnutne financovaním celej stavby.",
          "Prvý projekt vyhodnotíme a podľa výsledku budeme model rozširovať.",
        ],
      },
      {
        h: "Mestské nájomné bývanie",
        items: [
          "Pripravíme zásobník projektov, ktoré budú pripravené na financovanie zo ŠFRB a ďalších zdrojov.",
        ],
      },
      {
        h: "Súkromná výstavba s jasnými pravidlami",
        items: [
          "Zrýchlime procesy mesta pri kvalitne pripravených projektoch.",
          "Koniec papierovému parkovaniu. Nová výstavba musí mať reálne zabezpečené novovzniknuté parkovanie pre svojich obyvateľov a návštevníkov.",
          "Pri väčších projektoch budeme požadovať primeranú dopravnú kapacitu, zeleň a občiansku vybavenosť nad rámec zákona.",
          "Rozvojové lokality budeme posudzovať ako celky – nie ako jednotlivé bytové domy bez posúdenia dopravy, parkovania a možností okolia ako celku.",
        ],
      },
      {
        h: "Databáza možností",
        items: [
          "Zmapujeme mestské, štátne a súkromné objekty a parcely s potenciálom pre bývanie.",
          "Pripravíme harmonogram lokalít na 10 rokov, aby sa bývanie nestavalo náhodne.",
        ],
      },
    ],
    benefits: [
      "Väčší výber bývania a tlak na dostupnejšie ceny.",
      "Reálnu alternatívu pre mladé rodiny a ľudí, ktorí sa chcú do mesta vrátiť alebo prisťahovať.",
      "Nástroj na prilákanie nedostatkových profesií.",
      "Predvídateľný rozvoj nových štvrtí s infraštruktúrou.",
    ],
  },
  {
    no: "03",
    slug: "mhd",
    title: "Mestská hromadná doprava",
    tagline: "MHD stojí mesto státisíce eur. Urobme ju takú, aby ju ľudia chceli používať.",
    intro: "",
    sections: [
      {
        h: "MHD ako hlavný spôsob dopravy po meste",
        items: [
          "Mojím cieľom je 100 % dotácia mesta na MHD. Prvým krokom bude nastaviť sieť tak, aby autobusy jazdili tam, kde ich ľudia skutočne potrebujú a v časoch a intervaloch, ktoré dávajú zmysel.",
          "Autobus musí byť najjednoduchší spôsob, ako sa pohybovať po Ružomberku – rýchly, dostupný a použiteľný pre cestu do práce, školy, nemocnice, centra aj jednotlivých mestských častí.",
          "100 % dotovaná MHD odstráni potrebu kupovať a kontrolovať mestské lístky pri každej ceste, čo zjednoduší a urýchli nástup a skráti dĺžku jazdy.",
        ],
      },
      {
        h: "Dôstojné zastávky ako súčasť moderného mesta",
        items: [
          "Autobusová zastávka nemá byť len označenie pri ceste. Postupne chceme meniť zastávky na dôstojné, čisté a bezpečné miesta, ktoré chránia pred dažďom, vetrom aj slnkom.",
          "Nové a rekonštruované zastávky budú mať kvalitné striešky, sedenie, osvetlenie, prehľadné informácie o spojoch a bezbariérový prístup.",
          "Tam, kde to podmienky umožnia, budeme využívať aj zelené strechy podobné tým, ktoré poznáme zo zahraničných miest.",
          "Cieľom je, aby čakanie na autobus nebolo nepríjemnou povinnosťou, ale prirodzenou súčasťou kvalitnej verejnej dopravy.",
        ],
      },
    ],
    benefits: [
      "Dôstojnú MHD aj dôstojné zastávky, ktoré budú prirodzenou súčasťou moderného mesta.",
      "Dostupnejšiu verejnú dopravu pre všetkých – cieľom vyššej dotácie je postupne odstrániť finančnú bariéru cestovného.",
      "Lepšie a častejšie spojenie jednotlivých častí mesta podľa toho, ako ľudia Ružomberkom reálne cestujú.",
      "Skutočnú alternatívu k autu a menej áut v centre.",
    ],
  },
  {
    no: "04",
    slug: "cesty-a-udrzba",
    title: "Cesty, chodníky a základná údržba",
    tagline: "Mesto sa nespozná iba podľa veľkých projektov.",
    intro:
      "Kvalita mesta sa často ukáže na maličkostiach: pokosenej tráve, opravenom chodníku, svietiacej lampe, čistej zastávke alebo bezpečnom ihrisku. Základná údržba musí byť každodennou povinnosťou mesta.",
    sections: [
      {
        h: "Verejný plán opráv",
        items: [
          "Spracujeme plán údržby ciest, chodníkov a verejných plôch s popisom ich technického stavu.",
          "Zverejníme poradie opráv podľa objektívnych kritérií: bezpečnosť, poškodenie, využívanie a význam lokality.",
          "Obyvatelia budú vidieť, čo sa opraví v daný rok a čo je naplánované na ďalšie roky.",
        ],
      },
      {
        h: "Kontrola kvality odvedených prác",
        items: [
          "Pri každej zákazke bude verejne dohľadateľné, kto ju za mesto skontroloval a prevzal.",
          "Prácu mesto prevezme až vtedy, keď bude zodpovedať zadaniu, dohodnutej kvalite a technickým podmienkam.",
          "Ak sa pri kontrole zistia vady alebo nedorobky, budú zaznamenané a dodávateľ ich bude musieť odstrániť ešte pred riadnym prevzatím.",
        ],
      },
      {
        h: "Jednotný a kvalitný verejný priestor",
        items: [
          "Vytvoríme jasné pravidlá, podľa ktorých sa budú vyberať lavičky, koše, lampy, zastávky či zábradlia.",
          "Cieľom nie je, aby celé mesto vyzeralo rovnako, ale aby ulice, námestia a sídliská pôsobili upratane a premyslene.",
        ],
      },
      {
        h: "Ihriská, ktoré budú nové, bezpečné a udržiavané",
        items: [
          "Najskôr určíme, ktoré detské ihriská má mesto dlhodobo zachovať a rozvíjať.",
          "Pri týchto ihriskách sa budeme snažiť o kompletnú obnovu za nové moderné ihriská s maximálnym využitím európskych fondov.",
          "Cieľom je postupne vytvoriť sieť kvalitných, moderných a bezpečných ihrísk v jednotlivých častiach mesta.",
          "Pri nových ihriskách budeme myslieť aj na tieň, sedenie pre rodičov, zeleň, bezpečný prístup a podľa možností aj pitnú vodu.",
        ],
      },
    ],
    benefits: [
      "Predvídateľné opravy namiesto improvizácie.",
      "Vyššiu kvalitu mestských zákaziek.",
      "Bezpečnejšie chodníky, ihriská a ulice.",
      "Mesto, ktoré sa stará aj o bežné veci každý deň.",
    ],
  },
  {
    no: "05",
    slug: "zive-centrum",
    title: "Centrum, kde to žije",
    tagline: "Centrum potrebuje ľudí, nie iba novú dlažbu.",
    intro: "",
    sections: [
      {
        h: "Dohoda namiesto desaťročí konfliktu",
        items: [
          "Pri kľúčových súkromných pozemkoch v centre budeme rešpektovať vlastnícke práva a súčasne aktívne hľadať kompromis medzi verejným a súkromným záujmom.",
          "Mesto musí byť schopné rokovať, pomenovať svoje požiadavky a vytvoriť podmienky, aby zanedbané územia dostali novú tvár.",
        ],
      },
      {
        h: "Program pre živé centrum",
        items: [
          "Pravidelné malé kultúrne, komunitné, športové a rodinné podujatia.",
          "Jasné pravidlá pre malé sezónne terasy, trhy a komunitné aktivity.",
          "Kalendár podujatí koordinovaný tak, aby sa akcie zbytočne neprekrývali.",
        ],
      },
      {
        h: "Základné služby",
        items: [
          "Funkčné a dôstojné verejné WC.",
          "Viac košov a pravidelné čistenie.",
          "Lavičky, tieň a zeleň na miestach, kde sa ľudia prirodzene zastavujú.",
          "Lepšie navigačné a informačné prvky pre návštevníkov.",
        ],
      },
    ],
    benefits: [
      "Centrum, do ktorého sa oplatí prísť aj v dňoch, keď človek nič nevybavuje na úradoch.",
      "Viac zákazníkov pre miestne prevádzky.",
      "Príjemnejšie ulice, viac zelene a kvalitnejšie služby.",
      "Posun k riešeniam na dlhodobo problémových parcelách.",
    ],
  },
  {
    no: "06",
    slug: "transparentnost",
    title: "Otvorená a transparentná samospráva",
    tagline: "Mesto musí byť čitateľné zvonka aj zvnútra.",
    intro: "",
    sections: [
      {
        h: "Mestský majetok pod verejnou kontrolou",
        items: [
          "Zverejníme prehľad mestského majetku a jeho využitia.",
          "Pri významných predajoch a prenájmoch budeme preferovať otvorenú súťaž a verejné zdôvodnenie podmienok.",
          "Zámery nakladania s významným majetkom zverejníme v dostatočnom predstihu.",
        ],
      },
      {
        h: "Výberové konania",
        items: [
          "Vedúce miesta v mestských organizáciách a spoločnostiach budú obsadzované transparentne a odborne.",
          "Pri významných funkciách zavedieme verejné vypočutie kandidátov.",
          "Rodinné väzby nesmú byť kvalifikáciou na miesto na mestskom úrade alebo v mestských firmách.",
        ],
      },
      {
        h: "Zmluvy a obstarávanie",
        items: [
          "Budeme zverejňovať prehľadné informácie o väčších zákazkách: predmet, cena, dodávateľ, dodatky a konečná cena.",
          "Všetky zákazky a zmluvy budú nad rámec zákonných povinností zverejnené na jednom mieste.",
          "Pri veľkých investíciách zverejníme aj ekonomické zdôvodnenie a prevádzkové náklady.",
          "Mesto neuzavrie zmluvu s firmou bez známeho konečného užívateľa výhod ani pri malých zákazkách.",
        ],
      },
      {
        h: "Rozklikávací rozpočet",
        items: [
          "Občan uvidí, odkiaľ mesto získava peniaze a na čo ich míňa.",
          "Pri veľkých projektoch bude možné sledovať schválený rozpočet, vysúťaženú cenu a konečné čerpanie.",
        ],
      },
    ],
    benefits: [
      "Lepší prehľad o mestskom majetku a peniazoch.",
      "Férové výberové konania.",
      "Menej priestoru pre zákulisné dohody.",
      "Jednoduchšiu verejnú kontrolu.",
    ],
  },
  {
    no: "07",
    slug: "mesto-v-mobile",
    title: "Mesto v mobile a digitálne služby",
    tagline: "Jedna brána k mestským službám.",
    intro: "",
    sections: [
      {
        h: "Jedna mobilná aplikácia",
        items: [
          "Podnety a poruchy.",
          "Mestská hromadná doprava a odchody spojov.",
          "Parkovanie, odpad a harmonogramy.",
          "Kultúrne a športové podujatia.",
          "Odstávky, havárie a mimoriadne oznamy.",
          "Rozhodnutia zastupiteľstva a dôležité mestské dokumenty.",
        ],
      },
      {
        h: "Podnet občana od fotky po výsledok",
        items: [
          "Občan odošle fotografiu, polohu a krátky popis.",
          "Systém ukáže stav: prijaté – pridelené – rieši sa – vybavené.",
          "Pri podnete bude uvedený zodpovedný útvar a očakávaný termín odpovede.",
        ],
      },
      {
        h: "Garancia komunikácie",
        items: [
          "Mesto nastaví internú lehotu na prvú odpoveď občanovi pri bežnom elektronickom podnete.",
          "Ak sa vec nedá vyriešiť rýchlo, občan dostane aspoň informáciu, kto ju rieši a čo je ďalší krok.",
        ],
      },
    ],
    benefits: [
      "Menej telefonovania a hľadania správneho odboru.",
      "Prehľad o stave vlastného podnetu.",
      "Mestské služby na jednom mieste.",
      "Rýchlejšiu spätnú väzbu.",
    ],
  },
  {
    no: "08",
    slug: "rodiny-a-skolstvo",
    title: "Rodiny, deti, jasle a školstvo",
    tagline: "Mesto ako spoľahlivý partner rodiny a moderného vzdelávania.",
    intro: "",
    sections: [
      {
        h: "Dostupná starostlivosť od najmenších",
        items: [
          "Zachováme a rozvinieme mestské jasle tak, aby boli pre rodičov finančne dostupné, s menšími kolektívmi a kvalifikovaným personálom. Podporíme aj poldenné a adaptačné formy pobytu.",
          "Jasle budú dopĺňať triedy MŠ pre deti od 2 rokov, nie ich nahrádzať.",
          "Podporíme materské centrá ako dôležitú súčasť práce mesta s rodinami a malými deťmi.",
        ],
      },
      {
        h: "Materské školy – menej administratívy, viac podpory",
        items: [
          "Preveríme možnosť zlúčenia mestských MŠ pod jednu spoločnú právnu subjektivitu pre ich lepšie financovanie.",
          "Urobíme audit školských jedální a spoločných nákupov – šetriť na administratíve a prevádzke, nie na deťoch.",
          "Pre učiteľky zabezpečíme podporný tím psychológa a špeciálneho pedagóga a rozvoj inkluzívneho prostredia.",
        ],
      },
      {
        h: "Základné školy – mesto ako servisný partner",
        items: [
          "Mesto vytvorí školám spoločné odborné servisné zázemie pre právnu pomoc, eurofondy, obstarávania a podporné tímy.",
          "Riaditeľ má mať čas viesť a rozvíjať školu, nie byť údržbárom a administratívnym úradníkom.",
        ],
      },
      {
        h: "Bezpečné školy a priestory pre deti",
        items: [
          "Pri školách pripravíme bezpečné zóny „vylož a choď“ a preveríme sprevádzanie skupín mladších detí po bezpečných peších trasách.",
          "Tam, kde to prevádzka a bezpečnosť dovolia, otvoríme školské ihriská verejnosti.",
          "Budeme udržiavať a rozvíjať priestory pre rôzne vekové skupiny – detské a workoutové ihriská, pingpongové stoly a ďalšie možnosti pohybu.",
        ],
      },
      {
        h: "Rodiny a voľný čas",
        items: [
          "Zachováme adresnú pomoc na školské potreby a fond podpory rodín s dieťaťom so zdravotným znevýhodnením.",
          "Pripravíme mestskú požičovňu detských športových potrieb a mládežnícky participatívny rozpočet pre mladých vo veku 13 až 18 rokov.",
        ],
      },
    ],
    benefits: [
      "Dostupnejšiu starostlivosť o malé deti a väčší výber pre rodičov.",
      "Viac podpory pre učiteľov a menej administratívy pre vedenia škôl.",
      "Bezpečnejšie a otvorenejšie školy a viac možností pre voľný čas.",
      "Priestor, aby mladí ľudia mohli priamo rozhodovať o časti mestských peňazí.",
    ],
  },
  {
    no: "09",
    slug: "seniori",
    title: "Seniori a sociálne služby",
    tagline: "Dôstojný život v meste v každom veku.",
    intro: "",
    sections: [
      {
        h: "Starostlivosť čo najbližšie k domovu",
        items: [
          "Posilníme terénne sociálne služby a budeme sledovať čakacie lehoty na opatrovateľskú službu.",
          "Budeme rozvíjať denné aktivity, komunitné programy a podporu proti osamelosti.",
        ],
      },
      {
        h: "Mesto bez zbytočných bariér",
        items: [
          "Pri opravách chodníkov budeme systematicky vytvárať bezpečné bezbariérové prechody.",
          "Na trasách k zdravotníckym zariadeniam, zastávkam a obchodom doplníme chýbajúce lavičky a tieň.",
        ],
      },
      {
        h: "Bezpečnosť seniorov",
        items: [
          "Pravidelné informačné kampane proti telefonickým, internetovým a finančným podvodom.",
          "Praktické školenia digitálnej bezpečnosti v spolupráci s políciou, bankami a komunitnými organizáciami.",
        ],
      },
      {
        h: "Ľudia bez domova – pomoc smerujúca späť k samostatnému životu",
        items: [
          "Bezdomovectvo nechceme riešiť presúvaním ľudí z miesta na miesto.",
          "Cieľom bude individuálna sociálna práca – doklady, dávky, zdravie, dlhy, práca a cesta k stabilnému bývaniu.",
          "Pri nastavovaní systému chceme čerpať z praxe OZ Vagus – terénnu a nízkoprahovú pomoc, podporu pri práci a bývaní aj prevenciu straty bývania.",
        ],
      },
    ],
    benefits: [
      "Lepšie dostupné sociálne služby vrátane systematickej pomoci ľuďom bez domova.",
      "Bezpečnejší pohyb po meste.",
      "Viac možností aktívneho komunitného života.",
      "Väčšiu ochranu pred podvodmi.",
    ],
  },
  {
    no: "10",
    slug: "zdravotnictvo",
    title: "Zdravotníctvo a prevencia",
    tagline: "Mesto nemá všetky kompetencie, ale nemôže byť pasívne.",
    intro: "",
    sections: [
      {
        h: "Zdravotná mapa Ružomberka",
        items: [
          "Jednotný online prehľad ambulancií, lekární, pohotovostných služieb, AED a dôležitých kontaktov.",
          "Mapa zároveň pomôže identifikovať oblasti, kde dostupnosť služby slabne.",
        ],
      },
      {
        h: "AED a prvá pomoc",
        items: [
          "Zmapujeme všetky verejne dostupné AED a doplníme ich do digitálnej mapy.",
          "Podľa návštevnosti a rizika budeme postupne dopĺňať AED na mestské budovy a verejné miesta.",
          "Podporíme kurzy prvej pomoci pre školy, športové kluby a verejnosť.",
        ],
      },
      {
        h: "Detská pohotovosť",
        items: [
          "Využijeme všetky možnosti, aby sa v meste obnovila detská pohotovosť.",
        ],
      },
    ],
    benefits: [
      "Jednoduchší prehľad o dostupnej zdravotnej pomoci.",
      "Lepšiu pripravenosť na náhle zdravotné udalosti.",
      "Detskú pohotovosť priamo v meste.",
    ],
  },
  {
    no: "11",
    slug: "sport",
    title: "Šport a voľný čas",
    tagline: "Menej megalománie, viac každodenného športu.",
    intro: "",
    sections: [
      {
        h: "Mládežnícky šport",
        items: [
          "Pri mestských dotáciách nastavíme jasné a transparentné pravidlá s dôrazom na prácu s deťmi a mládežou.",
          "Budeme podporovať spoluprácu klubov so školami a medziškolské podujatia.",
        ],
      },
      {
        h: "Pasport športovísk",
        items: [
          "Zmapujeme technický stav mestských športovísk a ihrísk.",
          "Zverejníme viacročný plán opráv a modernizácie.",
        ],
      },
      {
        h: "Zimný štadión a veľké športové projekty",
        items: [
          "Najprv budeme riešiť technický stav, prevádzkové náklady a reálny dlh športovej infraštruktúry.",
          "Pri každom veľkom projekte budeme verejne prezentovať cenu výstavby aj budúce prevádzkové náklady.",
          "Uprednostníme realistické riešenia zodpovedajúce veľkosti mesta.",
        ],
      },
      {
        h: "Šport na sídliskách",
        items: [
          "Workoutové prvky, bežecké okruhy, malé multifunkčné ihriská, pumptrack a bezpečné plochy pre mladých podľa reálneho dopytu.",
          "Osvetlenie a bezpečný prístup tam, kde sa športuje aj večer.",
        ],
      },
    ],
    benefits: [
      "Viac možností športovať bez drahého členstva.",
      "Lepšie podmienky pre deti a mládež.",
      "Transparentné financovanie športu.",
      "Rozumné investície do veľkých športovísk.",
    ],
  },
  {
    no: "12",
    slug: "turizmus",
    title: "Turizmus: Hrabovo, Čutkovo, Vlkolínec",
    tagline: "Predávajme návštevníkovi celý Ružomberok, nie jednu atrakciu.",
    intro: "",
    sections: [
      {
        h: "Hrabovo",
        items: [
          "Pred ďalším intenzívnym rozvojom vyhodnotíme kapacitu dopravy, parkovania, vody, kanalizácie a verejného priestoru.",
          "Budeme koordinovať nové projekty tak, aby sa nezhoršila kvalita prostredia ani podmienky existujúcich prevádzok.",
          "Zlepšíme pešie, cyklistické a MHD spojenie s mestom.",
        ],
      },
      {
        h: "Čutkovo",
        items: [
          "Budeme chrániť rekreačný a prírodný charakter doliny.",
          "Zlepšíme orientáciu, bezpečnosť pohybu a napojenie na širšiu sieť trás.",
        ],
      },
      {
        h: "Vlkolínec",
        items: [
          "Prioritou bude funkčná technická infraštruktúra a spoľahlivé zásobovanie vodou.",
          "Rozvoj cestovného ruchu budeme nastavovať spolu s obyvateľmi a s rešpektom k charakteru lokality.",
          "Opravíme cestu vo Vlkolínci tak, aby zodpovedala významu a charakteru tejto lokality.",
        ],
      },
      {
        h: "Jeden turistický produkt",
        items: [
          "Ružomberok – Hrabovo – Malinô Brdo – Vlkolínec – Čutkovo budeme prezentovať ako prepojený zážitok.",
          "Podporíme spoločný kalendár podujatí, balíčky služieb a spoluprácu ubytovateľov, gastronómie a atrakcií.",
        ],
      },
    ],
    benefits: [
      "Dlhší pobyt návštevníkov a vyššiu útratu v meste.",
      "Lepšiu koordináciu rozvoja turistických lokalít.",
      "Ochranu prírodných a kultúrnych hodnôt.",
      "Viac príležitostí pre miestnych podnikateľov.",
    ],
  },
  {
    no: "13",
    slug: "cyklodoprava",
    title: "Cyklodoprava a peší pohyb",
    tagline: "Cyklotrasa má niekam viesť a chodník musí zostať chodníkom.",
    intro: "",
    sections: [
      {
        h: "Cyklogenerel a plán siete",
        items: [
          "Pripravíme mestský cyklogenerel, ktorý je uceleným plánom siete cyklodopravy, nie len izolovanými úsekmi.",
        ],
      },
      {
        h: "Prepojená sieť pre každodenný pohyb",
        items: [
          "Budeme pokračovať v prepájaní sídlisk s centrom, stanicami, školami, pracovnými zónami a rekreačnými lokalitami.",
          "Pri nových úsekoch budeme preferovať bezpečné oddelenie od chodcov tam, kde to priestor dovolí.",
          "Budeme koordinovať mestské trasy s regionálnymi cyklotrasami a okolitými obcami.",
        ],
      },
      {
        h: "Ulice navrhnuté ako celok",
        items: [
          "Pri rekonštrukciách ulíc budeme posudzovať peší pohyb, bicykel, MHD, autá, zeleň a parkovanie ako celok.",
          "Budeme viac počúvať odborníkov a inšpirovať sa riešeniami z iných slovenských miest.",
        ],
      },
    ],
    benefits: [
      "Bezpečnejšiu každodennú cyklistiku.",
      "Menej konfliktov medzi chodcami a cyklistami.",
      "Lepšie napojenie mesta na rekreačné trasy.",
      "Ulice navrhnuté ako celok.",
    ],
  },
  {
    no: "14",
    slug: "klima-zelen-voda",
    title: "Klíma, zeleň a voda",
    tagline: "Zeleň je infraštruktúra, nie dekorácia.",
    intro: "",
    sections: [
      {
        h: "Teplotná mapa mesta",
        items: [
          "Zmapujeme miesta, ktoré sa v lete najviac prehrievajú, a podľa toho budeme určovať priority tienenia a výsadby.",
        ],
      },
      {
        h: "Stromy tam, kde ich ľudia potrebujú",
        items: [
          "Prioritou budú školy, zastávky, ihriská, parkoviská, vnútrobloky a centrum.",
          "Pri výsadbe budeme vyberať druhy vhodné do mestského prostredia a počítať s ich budúcim rastom.",
        ],
      },
      {
        h: "Dažďová voda",
        items: [
          "Pri rekonštrukciách budeme preferovať priepustné povrchy tam, kde je to technicky možné.",
          "Podporíme dažďové záhrady, retenčné prvky a využívanie dažďovej vody pri mestských budovách.",
        ],
      },
      {
        h: "Zelené zastávky a strechy",
        items: [
          "Tam, kde je to technicky a ekonomicky rozumné, využijeme zelené strechy, tienenie a ďalšie prvky znižujúce prehrievanie.",
          "Vytvoríme verejný prehľad stromov, ich stavu, plánovanej údržby alebo výrubov a novej výsadby.",
          "Zavedieme harmonogram kosenia, ktorý sa bude riadiť podľa skutočného stavu porastu.",
        ],
      },
    ],
    benefits: [
      "Viac tieňa a príjemnejší verejný priestor.",
      "Lepšiu pripravenosť na horúčavy a prívalové dažde.",
      "Odbornejšiu starostlivosť o stromy.",
      "Menej prehrievané školy, zastávky a ihriská.",
    ],
  },
  {
    no: "15",
    slug: "bezpecne-mesto",
    title: "Bezpečné mesto",
    tagline: "Prevencia, viditeľná polícia a bezpečný verejný priestor.",
    intro: "",
    sections: [
      {
        h: "Mestská polícia medzi ľuďmi",
        items: [
          "Posilníme komunitný model práce v problémových lokalitách a pravidelný kontakt s obyvateľmi.",
          "Budeme vyhodnocovať, kde a v akom čase je prítomnosť hliadok najpotrebnejšia.",
        ],
      },
      {
        h: "Mapa problémových miest",
        items: [
          "Zmapujeme tmavé priechody, zanedbané zákutia, opakované vandalizmy a rizikové miesta.",
          "Budeme spájať osvetlenie, údržbu, mestskú políciu a sociálnu prácu.",
        ],
      },
      {
        h: "Bezpečnosť pri školách",
        items: [
          "Preveríme priechody, ranné dopravné špičky a parkovanie pri školách a škôlkach.",
          "Doplníme osvetlenie a fyzické bezpečnostné prvky tam, kde sú odôvodnené.",
          "Podporíme programy pre deti, rodičov a seniorov o podvodoch, kyberšikane a ochrane osobných údajov.",
        ],
      },
    ],
    benefits: [
      "Viditeľnejšiu mestskú políciu.",
      "Bezpečnejšie okolie škôl a priechodov.",
      "Rýchlejšie riešenie zanedbaných rizikových miest.",
      "Lepšiu prevenciu online podvodov.",
    ],
  },
  {
    no: "16",
    slug: "odpad",
    title: "Odpad a čisté mesto",
    tagline: "Čistota mesta nemá stáť na dobrovoľníkoch, ale na pravidelnej práci komunálnych služieb.",
    intro: "",
    sections: [
      {
        h: "Komunálne služby po mestských častiach",
        items: [
          "V komunálnych službách vytvoríme tím pracovníkov určený na pravidelnú údržbu jednotlivých mestských častí.",
          "Mesto rozdelíme do pracovných úsekov a nastavíme týždňové cykly na kosenie, zber odpadkov a kontrolu verejných priestorov.",
          "Základná čistota musí byť každodennou povinnosťou mesta, nie iba prácou aktivistov na brigáde.",
        ],
      },
      {
        h: "Odpad a kontajnerové stojiská",
        items: [
          "Frekvenciu vývozu odpadu budeme nastavovať podľa skutočnej potreby jednotlivých lokalít.",
          "Postupne obnovíme najhoršie kontajnerové stojiská a nastavíme ich pravidelné čistenie.",
          "Vytvoríme jasný systém na rýchle odstránenie čiernych skládok.",
          "Zavedieme spravodlivý systém zberu odpadu – platíš len za to, čo vyhodíš do komunálneho odpadu.",
        ],
      },
      {
        h: "Čistota podujatí a verejná kontrola",
        items: [
          "Pri verejných podujatiach budeme vyžadovať jasný plán čistoty, triedenia odpadu a upratania priestoru.",
          "Komunálne služby budú mať jasné úlohy a kontrolovateľné výsledky.",
        ],
      },
    ],
    benefits: [
      "Čistejšie sídliská, ulice a verejné priestory počas celého roka.",
      "Menej preplnených kontajnerov a odpadu pohodeného okolo nich.",
      "Pravidelnú starostlivosť o každú mestskú časť, nielen jednorazové brigády.",
      "Jasný systém, vďaka ktorému sa problémy neriešia až po opakovaných sťažnostiach.",
    ],
  },
  {
    no: "17",
    slug: "zvierata",
    title: "Zvieratá v meste",
    tagline: "Praktické riešenia pre majiteľov zvierat aj verejný priestor.",
    intro: "",
    sections: [
      { h: "Psie výbehy", items: ["Vyhodnotíme potrebu a umiestnenie bezpečných psích výbehov."] },
      { h: "Čistota pri venčení", items: ["Doplníme koše a zásobníky na sáčky tam, kde sa psy reálne venčia."] },
      {
        h: "Transparentné využitie výnosov",
        items: [
          "Preveríme možnosť transparentne viazať časť výnosov súvisiacich so psami na zlepšenie infraštruktúry pre zvieratá.",
        ],
      },
    ],
    benefits: [
      "Čistejší verejný priestor.",
      "Lepšie podmienky pre venčenie.",
      "Nové psie výbehy v mestských častiach.",
    ],
  },
  {
    no: "18",
    slug: "mestske-firmy",
    title: "Mestské firmy a hospodárenie",
    tagline: "Každé euro musí mať dôvod a výsledok.",
    intro: "",
    sections: [
      {
        h: "Audit a jasné ciele",
        items: [
          "Na začiatku volebného obdobia urobíme personálny, procesný a finančný audit významných mestských organizácií a spoločností.",
          "Cieľom nebude hľadanie vinníkov, ale pomenovanie problémov a návrh ich riešenia.",
          "Každá organizácia dostane merateľné ciele a verejný ročný odpočet.",
        ],
      },
      {
        h: "Veľké projekty s kalkulačkou",
        items: [
          "Pred schválením veľkej investície zverejníme nielen cenu výstavby, ale aj očakávané ročné prevádzkové náklady.",
          "Mesto bude posudzovať projekt aj podľa toho, či ho vie desaťročia prevádzkovať.",
        ],
      },
      {
        h: "Externé zdroje",
        items: [
          "Posilníme odbornú prípravu projektov pre eurofondy, štátne fondy a granty.",
          "Nebudeme podávať projekty len preto, že existuje výzva – každý projekt musí zapadať do dlhodobého plánu mesta.",
        ],
      },
    ],
    benefits: [
      "Efektívnejšie mestské organizácie.",
      "Menej neplánovaných prevádzkových nákladov.",
      "Lepšie využitie externých zdrojov.",
      "Verejný odpočet výsledkov.",
    ],
  },
  {
    no: "19",
    slug: "mestske-casti",
    title: "Mestské časti a susedstvá",
    tagline: "Každá časť mesta musí vedieť, čo sa v nej bude diať.",
    intro: "",
    sections: [
      {
        h: "Lokálne plány mestských častí",
        items: [
          "Ružomberok nie je iba centrum. Každá mestská časť a každé sídlisko potrebuje vlastný zoznam priorít a verejný harmonogram.",
          "Pre centrum a sídliská pripravíme lokálne balíky opatrení.",
          "Pri každom území budeme riešiť dopravu, MHD, chodníky, osvetlenie, zeleň, parkovanie, ihriská a konkrétne investičné potreby.",
        ],
      },
      {
        h: "Verejné stretnutia a odpočet",
        items: [
          "Aspoň jedenkrát ročne sa uskutoční verejné pracovné stretnutie vedenia mesta s obyvateľmi priamo v jednotlivých mestských častiach.",
          "Zverejníme odpočet: čo bolo sľúbené, čo je hotové, čo sa pripravuje a čo sa nepodarilo.",
        ],
      },
    ],
    benefits: [
      "Väčšiu pozornosť jednotlivým častiam mesta.",
      "Jasné lokálne priority.",
      "Priamy kontakt vedenia mesta s obyvateľmi.",
      "Verejný tlak na dokončenie rozpracovaných vecí.",
    ],
  },
  {
    no: "20",
    slug: "ruzomberok-2036",
    title: "Ružomberok 2036",
    tagline: "Štyri roky sú začiatok. Mesto potrebuje desaťročný smer.",
    intro:
      "Ak má Ružomberok zmeniť trend, nestačí plánovať od volieb k voľbám. Potrebujeme vedieť, ako má mesto vyzerať o desať rokov a čo musíme začať pripravovať už dnes.",
    sections: [
      {
        h: "Smer do roku 2036",
        items: [
          "Bývanie: približne 1 000 nových bytov rôznych kategórií.",
          "Práca: nové investície a tisíce pracovných príležitostí v horizonte desaťročia.",
          "Obyvateľstvo: stabilizácia a postupné vytváranie podmienok na návrat k rastu.",
          "MHD: sieť, ktorá je použiteľná, jednoduchá a finančne udržateľná.",
          "Centrum: živé ulice, nové funkcie problémových území a viac každodenného života.",
          "Verejný priestor: systematická údržba, bezpečné chodníky a kvalitné ihriská.",
          "Školstvo a rodiny: dostupné služby, inklúzia a lepšie podmienky pre deti.",
          "Turizmus: prepojený produkt Hrabovo – Malinô Brdo – Vlkolínec – Čutkovo – mesto.",
          "Klíma: viac tieňa, vody v krajine a odolnejší verejný priestor.",
          "Radnica: otvorená, digitálna a kontrolovateľná samospráva.",
        ],
      },
      {
        h: "Záväzok odpočtu",
        items: [
          "Každý rok zverejníme verejný odpočet programu: SPLNENÉ / ROZPRACOVANÉ / NESPLNENÉ. Pri nesplnenom bode uvedieme dôvod a ďalší postup.",
        ],
      },
    ],
    benefits: [],
  },
];

export const getChapter = (slug) => chapters.find((c) => c.slug === slug) || null;
